<template>
  <section :data-view-ready="!(building === null || onboardingLoading)">
    <div class="floating-wrapper">
      <coozzy-button
        v-if="!onboardingLoading"
        :disabled="!hasChanged || dataProcessing"
        class="floating-button"
        data-cy="building-save-floating-btn"
        design="transparent"
        @click="saveData">
        <coozzy-save-icon />
      </coozzy-button>
    </div>
    <div class="row">
      <div
        v-if="building === null || onboardingLoading"
        class="col-12 text-center">
        <coozzy-spinner class="m-3" />
      </div>
      <div
        v-else
        class="col-12">
        <coozzy-page-title
          :sub-title="$t('message.propertiesView.tabs.property') +': ' + propertyName + ' | ' + $t('message.onBoarding.buildings.address') + ': ' + building.address.street + ' ' + building.address.streetNumber + ', ' +building.address.zip + ' ' + building.address.city"
          :title="$t('message.onBoarding.buildings.title') + ': ' + building.name" />
        <!-- Action buttons on mobile -->
        <div class="row d-md-none">
          <div class="col-12 mb-2">
            <router-link
              :to="toPropertyClicked()">
              <coozzy-button
                class="w-100"
                data-cy="building-to-property-btn"
                design="secondary">
                {{ $t('message.onBoardingTabs.toProperty') }}
              </coozzy-button>
            </router-link>
          </div>
          <div class="col-12 mb-2">
            <coozzy-button
              :disabled="!hasChanged || dataProcessing"
              class="w-100"
              data-cy="building-save-btn"
              design="primary"
              @click="saveData">
              {{ $t('message.generic.form.save') }}
            </coozzy-button>
          </div>
          <div class="col-12 mb-2">
            <coozzy-button
              class="w-100"
              data-cy="building-read-view-btn"
              design="secondary"
              @click="showConfirm">
              {{ $t('message.generic.readView') }}
            </coozzy-button>
          </div>
        </div>
        <b-tabs
          v-model="tabIndex"
          class="mb-3"
          @activate-tab="onActivateTab">
          <b-tab
            class="p-0"
            :lazy="shouldLazyEditableTabs">
            <template #title>
              <router-link
                data-cy="building-tab-building"
                :to="getBuildingRoute('building')">
                <span>{{ $t('message.onBoardingTabs.building') }}</span>
              </router-link>
            </template>
            <create-building-tab ref="create-building-view" />
          </b-tab>
          <b-tab
            class="p-0"
            :lazy="shouldLazyEditableTabs">
            <template #title>
              <router-link
                data-cy="building-tab-objects"
                :to="getBuildingRoute('objects')">
                <span>{{ $t('message.onBoardingTabs.objects') }}</span>
              </router-link>
            </template>
            <create-objects-tab
              :key="elementSelected"
              ref="create-objects-view"
              :element-selected="elementSelected"
              :object-filters="objectFilters"
              @selected-object="updateElementSelected"
              @change-filter="updateWithFilter" />
          </b-tab>
          <b-tab
            v-if="propertyType === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP'"
            class="p-0"
            :lazy="shouldLazyEditableTabs">
            <template #title>
              <router-link
                data-cy="building-tab-condominiums"
                :to="getBuildingRoute('condominium')">
                <span>{{ $t('message.onBoardingTabs.condominium') }}</span>
              </router-link>
            </template>
            <create-condominium-tab
              ref="create-condominium-tab"
              :condominium-filters="condominiumFilters"
              :element-selected="elementSelected"
              @selected-condominium="updateElementSelected"
              @change-filter="updateWithFilter" />
          </b-tab>
          <b-tab
            v-else
            class="p-0"
            :lazy="shouldLazyEditableTabs">
            <template #title>
              <router-link
                data-cy="building-tab-tenants"
                :to="getBuildingRoute('tenants')">
                <span>{{ $t('message.onBoardingTabs.tenants') }}</span>
              </router-link>
            </template>
            <create-tenants-tab
              ref="create-tenants-tab"
              :element-selected="elementSelected"
              :tenant-filters="tenantFilters"
              @selected-tenancy="updateElementSelected"
              @change-filter="updateWithFilter" />
          </b-tab>
          <b-tab
            class="p-0"
            :lazy="shouldLazyEditableTabs">
            <template #title>
              <router-link
                data-cy="building-tab-devices"
                :to="getBuildingRoute('devices')">
                <span>{{ $t('message.onBoardingTabs.devices') }}</span>
              </router-link>
            </template>
            <create-devices-tab
              :key="elementSelected"
              ref="create-devices-tab"
              :add-new-item-device="addNewItemDevice"
              :device-filters="deviceFilters"
              :element-selected="$route.query.elementSelected ? $route.query.elementSelected : elementSelected"
              :property-id="propertyId"
              @selected-device="updateElementSelected"
              @change-filter="updateWithFilter" />
          </b-tab>
          <b-tab
            v-if="isInvestPlanEnabled"
            lazy
            class="p-0">
            <template #title>
              <router-link
                data-cy="building-tab-investment-planning"
                :to="getBuildingRoute('investmentPlanning') !== undefined ? getBuildingRoute('investmentPlanning') : ''">
                <span>{{ $t('message.onBoarding.investmentPlanning.title') }}</span>
              </router-link>
            </template>
            <building-invest-plan-tab
              ref="investPlanTab"
              :add-new-component="addNewComponent"
              :invest-plan-filters="investPlanFilters"
              @change-filter="updateWithFilter" />
          </b-tab>
          <b-tab
            v-if="(isOwnerModule && isCompanyAdmin) || !isOwnerModule"
            class="p-0"
            lazy>
            <template #title>
              <router-link
                data-cy="building-tab-contacts"
                :to="getBuildingRoute('contacts')">
                <span>{{ $t('message.onBoardingTabs.contacts') }}</span>
              </router-link>
            </template>
            <create-contacts-tab
              ref="create-contacts-tab"
              @has-change="onContactsHasChanges" />
          </b-tab>
          <b-tab
            class="p-0">
            <template #title>
              <router-link
                class="tab_title_color"
                data-cy="building-tab-history"
                :to="getBuildingRoute('history')">
                <span>{{ $t('message.onBoardingTabs.history') }}</span>
                <span
                  v-if="hasNotification"
                  class="small-dot" />
              </router-link>
            </template>
            <history-tab
              ref="activitiesTab"
              :building-prop="building"
              :device-list="buildingDevices.concat(objectDevices)"
              :history-filters="historyFilters"
              :route-name="$route.name"
              :element-selected="elementSelected"
              :prev-route="prevRoute"
              :default-text="defaultText"
              :default-type="defaultType"
              :default-creator="defaultCreator"
              :default-assigne="defaultAssignee"
              :default-priority="defaultPriority"
              :default-status="defaultStatus"
              :reference-ids="referenceIds"
              :history-sub-tab="historySubTab"
              @has-notification="updateHasNotification"
              @change-filter="updateWithFilter" />
          </b-tab>
          <b-tab
            v-if="isPortalLinkEnabled"
            class="p-0">
            <template #title>
              <router-link
                data-cy="building-tab-portal-links"
                :to="getBuildingRoute('portalLinks') !== undefined ? getBuildingRoute('portalLinks') : ''">
                <span>{{ $t('message.portalTab.title') }}</span>
              </router-link>
            </template>
            <building-portal-links-tab
              ref="portalLinksTab" />
          </b-tab>
          <template #tabs-end>
            <b-nav-item
              class="d-none d-md-block item-button">
              <coozzy-button
                :disabled="!hasChanged || dataProcessing"
                class="nav-button nav-button-center"
                data-cy="building-save-btn"
                design="primary"
                @click="saveData">
                {{ $t('message.generic.form.save') }}
              </coozzy-button>
            </b-nav-item>
            <b-nav-item
              class="d-none d-md-block item-button"
              href="#">
              <coozzy-button
                class="nav-button nav-button-right"
                data-cy="building-read-view-btn"
                design="secondary"
                @click="showConfirm">
                {{ $t('message.generic.readView') }}
              </coozzy-button>
            </b-nav-item>
            <b-nav-item
              class="d-none d-md-block item-button"
              href="#">
              <router-link
                :to="toPropertyClicked()">
                <coozzy-button
                  class="nav-button nav-button-right adaptToPropertyButton"
                  data-cy="building-to-property-btn"
                  design="secondary">
                  {{ $t('message.onBoardingTabs.toProperty') }}
                </coozzy-button>
              </router-link>
            </b-nav-item>
          </template>
        </b-tabs>
      </div>
    </div>
    <b-modal
      :id="'modal-saveAfterInactive'"
      :ref="`modal-saveAfterInactive`"
      :title="$t('message.onBoarding.buildings.objects.inactivePopup.title')"
      hide-footer
      no-close-on-backdrop>
      <div class="col">
        <p>{{ $t('message.onBoarding.buildings.objects.inactivePopup.text', { objectId: objectActivated }) }}</p>
      </div>
      <div class="col">
        <coozzy-button
          class="float-right mb-0"
          design="green"
          size="small"
          @click="$bvModal.hide('modal-saveAfterInactive')">
          {{ $t('message.onBoarding.buildings.objects.inactivePopup.ok') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      ref="modal-confirmation"
      :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
      hide-footer
      no-close-on-backdrop>
      <div class="row mb-2">
        <div class="col">
          <p class="mb-3">
            {{ $t('message.onBoardingTabs.confirmModal.confirmationText') }}
          </p>
          <coozzy-button
            data-cy="building-discard-changes-btn"
            design="green"
            size="normal"
            @click="discardChanges">
            {{ $t('message.generic.discardChanges') }}
          </coozzy-button>
          <coozzy-button
            :disabled="dataProcessing"
            class="float-right"
            data-cy="building-save-confirm-btn"
            design="green"
            size="normal"
            @click="saveData">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
          <coozzy-button
            class="float-right mr-3 border"
            data-cy="building-cancel-confirm-btn"
            design="transparent"
            size="normal"
            @click="cancelLeave">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import Vue from 'vue'
import { mapState, mapActions } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import CoozzyButton from '../../framework/components/button/CoozzyButton'
import CreateBuildingTab from '../tabs/CreateBuildingTab'
import CreateObjectsTab from '../tabs/CreateObjectsTab'
import CreateTenantsTab from '../tabs/CreateTenantsTab'
import CreateCondominiumTab from '../tabs/CreateCondominiumTab'
import CreateDevicesTab from '../tabs/CreateDevicesTab'
import { user } from '@/mixins/user'
import { onboarding } from '@/mixins/onboarding'
import CoozzySpinner from '../../framework/components/misc/CoozzySpinner'
import CreateContactsTab from '../tabs/CreateContactsTab'
import CoozzySaveIcon from '@/framework/components/icons/CoozzySaveIcon'
import HistoryTab from '@/property/tabs/HistoryTab.vue'
import { routeChecks } from '@/mixins/routeChecks'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle'
import BuildingInvestPlanTab from '@/building/tabs/components/BuildingInvestPlanTab.vue'
import BuildingPortalLinksTab from '@/building/tabs/BuildingPortalLinksTab.vue'
import { investmentPlanning } from '@/mixins/investmentPlanning'

export default {
  name: 'OnboardingView',
  components: {
    BuildingPortalLinksTab,
    BuildingInvestPlanTab,
    CoozzyPageTitle,
    CoozzySaveIcon,
    CoozzySpinner,
    CoozzyButton,
    CreateBuildingTab,
    CreateObjectsTab,
    CreateDevicesTab,
    CreateTenantsTab,
    CreateCondominiumTab,
    CreateContactsTab,
    HistoryTab
  },
  mixins: [user, onboarding, routeChecks, investmentPlanning],
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('onboarding/setOpenedCollapses')
    this.nextPath = next
    this.prevRoute = from
    if (this.hasChanged) {
      this.$refs['modal-confirmation'].show()
    } else {
      const next = this.nextPath
      this.nextPath = null
      next()
    }
  },
  beforeRouteUpdate(to, from, next) {
    this.prevRoute = to
    const toView = typeof to.query.view === 'string' ? to.query.view : this.view
    const fromView = typeof from.query.view === 'string' ? from.query.view : this.view
    const targetTabIndex = this.getTabIndexByView(toView)
    const currentTabIndex = this.getTabIndexByView(fromView)
    if (targetTabIndex !== currentTabIndex && !this.canLeaveTab(currentTabIndex)) {
      next(false)
      this.$nextTick(() => {
        this.isRevertingTabChange = true
        this.openSelectedTabs()
      })
      return
    }
    next()
  },
  props: {
    elementSelected: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: null
    },
    view: {
      type: String,
      default: 'building'
    },
    objectFilters: {
      type: String,
      default: ''
    },
    deviceFilters: {
      type: String,
      default: ''
    },
    historyFilters: {
      type: String,
      default: ''
    },
    tenantFilters: {
      type: String,
      default: ''
    },
    condominiumFilters: {
      type: String,
      default: ''
    },
    addNewItemDevice: {
      type: [Boolean, String],
      default: false
    },
    addNewComponent: {
      type: [Boolean, String],
      default: false
    },
    historySubTab: {
      type: String,
      default: 'tickets'
    },
    investPlanFilters: {
      type: String,
      default: ''
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.onBoarding.buildings.title') + ' ' + this.buildingName
    }
  },
  data() {
    return {
      tabIndex: 0,
      loading: false,
      nextPath: null,
      objectActivated: null,
      dataProcessing: false,
      hasNotification: false,
      isInvestPlanEnabled: false,
      isRevertingTabChange: false,
      shouldLazyEditableTabs: true,
      prevRoute: this.$route,
      defaultText: '',
      defaultPriority: '',
      defaultType: '',
      referenceIds: '',
      defaultAssignee: '',
      defaultCreator: '',
      defaultStatus: '',
      beforeUnloadHandler: null
    }
  },
  computed: {
    ...mapState('onboarding', ['errors']),
    buildingName() {
      return this.building && this.building.name ? this.building.name : ''
    },
    propertyName() {
      if (this.properties[0] === null || typeof this.properties[0] === 'undefined') {
        return '-'
      }
      return this.properties[0].name
    },
    propertyId() {
      return this.properties[0] ? this.properties[0].id : null
    },
    propertyType() {
      return this.properties.length > 0 ? this.properties[0].type : ''
    },
    isPortalLinkEnabled() {
      return this.$store.getters['onboarding/getPortalLinkEnabled']
    }
  },
  watch: {
    errors: function (newVal) {
      if (newVal.length > 0) {
        // Go to correct tab
        if (this.errors[0].entityType === 'building') {
          this.tabIndex = 0
        } else {
          this.tabIndex = 1
        }
      }
    },
    view: function () {
      if (this.isInvestPlanEnabled !== null) {
        this.openSelectedTabs()
      }
    },
    isInvestPlanEnabled: function () {
      this.openSelectedTabs()
    },
    tabIndex: function (newVal, oldVal) {
      if (this.isRevertingTabChange) {
        this.isRevertingTabChange = false
        return
      }

      if (typeof oldVal === 'number' && oldVal !== newVal && !this.canLeaveTab(oldVal)) {
        this.isRevertingTabChange = true
        this.tabIndex = oldVal
        return
      }

      let newView = 'building'
      const propertyType = this.propertyType === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP'
      if (newVal === 0) {
        newView = 'building'
      } else if (newVal === 1) {
        newView = 'objects'
      } else if (newVal === 2) {
        if (propertyType) {
          newView = 'condominium'
        } else {
          newView = 'tenants'
        }
      } else if (newVal === 3) {
        newView = 'devices'
      } else if (newVal === 4 && this.isInvestPlanEnabled) {
        newView = 'investmentPlanning'
      } else if ((newVal === 5 && this.isInvestPlanEnabled) || (newVal === 4 && !this.isInvestPlanEnabled)) {
        newView = 'contacts'
      } else if ((newVal === 6 && this.isInvestPlanEnabled) || (newVal === 5 && !this.isInvestPlanEnabled)) {
        newView = 'history'
      } else if ((newVal === 7 && this.isInvestPlanEnabled) || (newVal === 6 && !this.isInvestPlanEnabled)) {
        newView = 'portalLinks'
      }

      if (this.view !== newView) {
        if (this.isOwnerModule) {
          this.$router.push({
            name: 'OwnerOnboardingView',
            params: { id: this.id },
            query: {
              view: newView,
              elementSelected: this.elementSelected,
              from: this.from,
              objectFilters: this.objectFilters,
              tenantFilters: this.tenantFilters,
              condominiumFilters: this.condominiumFilters,
              deviceFilters: this.deviceFilters,
              historyFilters: this.historyFilters
            }
          }, () => {
          })
        } else if (this.isAdminModule) {
          this.$router.push({
            name: 'AdminOnboardingView',
            params: { id: this.id },
            query: {
              view: newView,
              elementSelected: this.elementSelected,
              from: this.from,
              objectFilters: this.objectFilters,
              tenantFilters: this.tenantFilters,
              condominiumFilters: this.condominiumFilters,
              deviceFilters: this.deviceFilters,
              historyFilters: this.historyFilters
            }
          }, () => {
          })
        } else if (this.isAssetModule) {
          this.$router.push({
            name: 'AssetOnboardingView',
            params: { id: this.id },
            query: {
              view: newView,
              elementSelected: this.elementSelected,
              from: this.from,
              objectFilters: this.objectFilters,
              tenantFilters: this.tenantFilters,
              condominiumFilters: this.condominiumFilters,
              deviceFilters: this.deviceFilters,
              historyFilters: this.historyFilters
            }
          }, () => {
          })
        } else if (this.isAccountingModule) {
          this.$router.push({
            name: 'AccountingOnboardingView',
            params: { id: this.id },
            query: {
              view: newView,
              elementSelected: this.elementSelected,
              from: this.from,
              objectFilters: this.objectFilters,
              tenantFilters: this.tenantFilters,
              condominiumFilters: this.condominiumFilters,
              deviceFilters: this.deviceFilters,
              historyFilters: this.historyFilters
            }
          }, () => {
          })
        }
      }
    },
    onboardingLoading: function () {
      this.$store.dispatch('onboarding/setLoadingFinish', true)
      this.$nextTick(() => {
        let scrollpos = localStorage.getItem('scrollpos')
        if (scrollpos) {
          window.scrollTo(0, parseInt(scrollpos))
        }
      })
    }
  },
  created() {
    this.$store.dispatch('onboarding/setLoadingFinish', false)
    this.$store.dispatch('onboarding/setLoading', true)
  },
  mounted() {
    this.loadData()
  },
  beforeDestroy: function () {
    localStorage.removeItem('buildingObject-nbrPerPage')
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }
    this.$store.dispatch('onboarding/clear')
  },
  methods: {
    ...mapActions('onboarding', ['load']),
    onActivateTab(newTabIndex, prevTabIndex, bvEvent) {
      if (newTabIndex === prevTabIndex) {
        return
      }

      if (!this.canLeaveTab(prevTabIndex)) {
        bvEvent.preventDefault()
      }
    },
    getTabIndexByView(view) {
      if (view === 'building') {
        return 0
      }
      if (view === 'objects') {
        return 1
      }
      if (view === 'tenants' || view === 'condominium') {
        return 2
      }
      if (view === 'devices') {
        return 3
      }
      if (view === 'investmentPlanning' && this.isInvestPlanEnabled) {
        return 4
      }
      if (view === 'contacts') {
        return this.isInvestPlanEnabled ? 5 : 4
      }
      if (view === 'history') {
        return this.isInvestPlanEnabled ? 6 : 5
      }
      if (view === 'portalLinks') {
        return this.isInvestPlanEnabled ? 7 : 6
      }
      return this.tabIndex
    },
    canLeaveTab(tabIndex) {
      const portalLinksTabIndex = this.isPortalLinkEnabled ? this.getTabIndexByView('portalLinks') : -1
      const editableTabs = [0, 1, 2, 3, portalLinksTabIndex]
      if (!editableTabs.includes(tabIndex)) {
        return true
      }

      this.commitFocusedField()
      localStorage.setItem('error-scroll-progress', 'false')
      const currentTabInvalid = this.isTabInvalid(tabIndex)
      if (currentTabInvalid) {
        if (!this.shouldSuppressGenericValidationToast(tabIndex)) {
          Vue.toasted.show(this.$t('message.savingErrors.validationError3'), { type: 'error' })
        }
        return false
      }

      return true
    },
    shouldSuppressGenericValidationToast(tabIndex) {
      if (tabIndex === 1 && this.$refs['create-objects-view']?.suppressGenericValidationToast) {
        return true
      }
      if (tabIndex === 2 && this.$refs['create-tenants-tab']?.suppressGenericValidationToast) {
        return true
      }
      return false
    },
    isTabInvalid(tabIndex) {
      if (tabIndex === 0) {
        return this.$refs['create-building-view'] && this.$refs['create-building-view'].isInvalid()
      }
      if (tabIndex === 1) {
        return this.$refs['create-objects-view'] && this.$refs['create-objects-view'].isInvalid()
      }
      if (tabIndex === 2) {
        if (this.propertyType === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP') {
          return this.$refs['create-condominium-tab'] && this.$refs['create-condominium-tab'].isInvalid()
        }
        return this.$refs['create-tenants-tab'] && this.$refs['create-tenants-tab'].isInvalid()
      }
      if (tabIndex === 3) {
        return this.$refs['create-devices-tab'] && this.$refs['create-devices-tab'].isInvalid()
      }
      if (this.isPortalLinkEnabled && tabIndex === this.getTabIndexByView('portalLinks')) {
        return this.$refs.portalLinksTab && this.$refs.portalLinksTab.isInvalid()
      }
      return false
    },
    commitFocusedField() {
      const activeElement = document && document.activeElement
      if (activeElement && typeof activeElement.blur === 'function') {
        activeElement.blur()
      }
    },
    async loadData() {
      const rspInvest = await this.getBuildingEnabledInvestPlan([this.id])
      this.isInvestPlanEnabled = rspInvest
      this.openSelectedTabs()
      localStorage.removeItem('toReadView')
      localStorage.removeItem('saveAfterInactive')
      await this.load(this.id)
      if (!this.beforeUnloadHandler) {
        this.beforeUnloadHandler = (event) => {
          if (this.hasChanged) {
            event.returnValue = this.$t('message.generic.discardChanges')
          }
        }
        window.addEventListener('beforeunload', this.beforeUnloadHandler)
      }
      if (this.historyFilters !== '') {
        const objFilter = JSON.parse(this.historyFilters)
        this.defaultText = objFilter.defaultText || ''
        this.defaultType = objFilter.defaultType || ''
        this.defaultStatus = objFilter.defaultStatus || ''
        this.defaultCreator = objFilter.defaultCreator || ''
        this.defaultAssigne = objFilter.defaultAssigne || ''
        this.defaultPriority = objFilter.defaultPriority || ''
        this.referenceIds = objFilter.referenceIds || ''
      }
    },
    onContactsHasChanges(value) {
      if (value !== false) {
        this.$store.dispatch('onboarding/activateChanged')
      }
    },
    updateHasNotification(value) {
      this.hasNotification = value
    },
    toPropertyClicked() {
      if (this.isOwnerModule) {
        return {
          name: 'OwnerPropertyEditingView',
          params: { id: this.properties[0].id }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminPropertyEditingView',
          params: { id: this.properties[0].id }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetPropertyEditingView',
          params: { id: this.properties[0].id }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingPropertyEditingView',
          params: { id: this.properties[0].id }
        }
      }
    },
    addNewDevice() {
      this.buildings[0].devices.push({ internalId: Date.now() })
      this.$nextTick(() => {
        const indexDevice = this.buildings[0].devices.length - 1
        const domRect = this.$el.querySelector('#device-' + indexDevice)
        domRect.scrollIntoView(true)
        this.$el.querySelector('#device-' + indexDevice + ' input').focus()
      })
    },
    autocompletePlaceChanged(data) {
      this.updateWithAddressComponents(data.address_components)
    },
    closeModal() {
      if (this.$refs['modal-confirmation']) {
        this.$refs['modal-confirmation'].hide()
      }
    },
    cancelLeave() {
      this.closeModal()
      if (typeof this.nextPath === 'function') {
        this.nextPath(false)
      }
      this.nextPath = null
    },
    // countDownChanged(dismissCountDown) {
    //   this.updateDismissCountDown(dismissCountDown)
    // },
    discardChanges() {
      this.closeModal()
      this.resetChanged()
      if (this.nextPath !== null) {
        const next = this.nextPath
        this.nextPath = null
        next()
      } else if (localStorage.getItem('toReadView')) {
        this.redirectToReadView()
      } else {
        this.$router.go(-1)
      }
    },
    openSelectedTabs() {
      if (this.view === 'building') {
        this.tabIndex = 0
      } else if (this.view === 'objects') {
        this.tabIndex = 1
      } else if (this.view === 'tenants' || this.view === 'condominium') {
        this.tabIndex = 2
      } else if (this.view === 'devices') {
        this.tabIndex = 3
      } else if (this.view === 'investmentPlanning' && this.isInvestPlanEnabled) {
        this.tabIndex = 4
      } else if (this.view === 'contacts') {
        this.tabIndex = this.isInvestPlanEnabled ? 5 : 4
      } else if (this.view === 'history') {
        this.tabIndex = this.isInvestPlanEnabled ? 6 : 5
      } else if (this.view === 'portalLinks') {
        this.tabIndex = this.isInvestPlanEnabled ? 7 : 6
      }
    },
    removeDevice(index) {
      this.$delete(this.buildings[0].devices, index)
    },
    async saveData() {
      // dataProcessing is used to disable the save button to avoid multiple clicks
      this.dataProcessing = true
      this.shouldLazyEditableTabs = false
      await this.$nextTick()
      this.commitFocusedField()
      localStorage.setItem('error-scroll-progress', 'false')
      // check if all tabs are valid
      const isBuildingViewValid = !this.isTabInvalid(0)
      const isObjectsViewValid = !this.isTabInvalid(1)
      const isTenantsViewValid = this.propertyType === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP' ? true : !this.isTabInvalid(2)
      const isCondominiumsViewValid = this.propertyType === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP' ? !this.isTabInvalid(2) : true
      const isDevicesViewValid = !this.isTabInvalid(3)
      const portalLinksTabIndex = this.isPortalLinkEnabled ? this.getTabIndexByView('portalLinks') : -1
      const isPortalLinksViewValid = portalLinksTabIndex === -1 ? true : !this.isTabInvalid(portalLinksTabIndex)
      console.log('Validation state: ' + JSON.stringify({
        isBuildingViewValid: isBuildingViewValid,
        isObjectViewValid: isObjectsViewValid,
        isTenantViewValid: isTenantsViewValid,
        isCondominiumsViewValid: isCondominiumsViewValid,
        isDevicesViewValid: isDevicesViewValid,
        isPortalLinksViewValid: isPortalLinksViewValid
      }))
      // I think create-objects-view has multiple child levels it take some time to verify validation
      this.$nextTick(async () => {
        if (isBuildingViewValid && isObjectsViewValid && isTenantsViewValid && isCondominiumsViewValid && isDevicesViewValid && isPortalLinksViewValid) {
          await this.$store.dispatch('onboarding/setOpenedCollapses')
          this.closeModal()
          localStorage.setItem('scrollpos', window.scrollY)
          // const result = await this.saveOnboarding()
          // Abort if saving failed
          // if (result === false) {
          // We use a nextTick + timeout to be absolutely after expanding the necessary collapses
          // if (this.$refs['create-building-view']) {
          //   this.$refs['create-building-view'].isInvalid()
          // }
          // if (this.$refs['create-objects-view']) {
          //   this.$refs['create-objects-view'].isInvalid()
          // }
          // if (this.propertyType !== 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP' && this.$refs['create-tenants-tab']) {
          //   this.$refs['create-tenants-tab'].isInvalid()
          // }
          // if (this.propertyType === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP' && this.$refs['create-condominium-tab']) {
          //   this.$refs['create-condominium-tab'].isInvalid()
          // }
          // if (this.$refs['create-devices-tab']) {
          //   this.$refs['create-devices-tab'].isInvalid()
          // }
          this.dataProcessing = false
          if (this.$refs['create-contacts-tab']) {
            await this.$refs['create-contacts-tab'].savePendingContactPersonChanges()
          }
          await this.$store.dispatch('onboarding/setLoading', true)
          // Sync and save building value when on investment planning tab
          if (this.view === 'investmentPlanning' && this.isInvestPlanEnabled) {
            this.$refs.investPlanTab?.$refs?.buildingComponentCosts?.syncBuildingValueToStore?.()
            await this.$store.dispatch('onboarding/persistBuildingValue')
          }
          const result = await this.saveOnboarding()
          if (result === false) {
            return false
          }
          if (this.building?.id) {
            await this.refreshBuildingContacts(this.building.id)
          }
          if (this.$refs['create-contacts-tab']?.syncContactsDisplayAfterBuildingLinksChange) {
            await this.$refs['create-contacts-tab'].syncContactsDisplayAfterBuildingLinksChange()
          }
          // return
          // }
          if (localStorage.getItem('toReadView')) {
            localStorage.removeItem('toReadView')
            this.redirectToReadView()
          } else if (localStorage.getItem('saveAfterInactive')) {
            this.objectActivated = localStorage.getItem('saveAfterInactive')
            this.$refs['modal-saveAfterInactive'].show()
            localStorage.removeItem('saveAfterInactive')
          } else if (localStorage.getItem('newDeviceId') !== null && this.view === 'devices') {
            const query = {
              view: this.view,
              elementSelected: localStorage.getItem('newDeviceId'),
              objectFilters: this.objectFilters,
              tenantFilters: this.tenantFilters,
              condominiumFilters: this.condominiumFilters,
              deviceFilters: this.deviceFilters,
              historyFilters: this.historyFilters
            }
            if (this.isOwnerModule) {
              this.$router.push({
                name: 'OwnerOnboardingView',
                params: { id: this.id },
                query: query
              })
            } else if (this.isAdminModule) {
              this.$router.push({
                name: 'AdminOnboardingView',
                params: { id: this.id },
                query: query
              })
            } else if (this.isAssetModule) {
              this.$router.push({
                name: 'AssetOnboardingView',
                params: { id: this.id },
                query: query
              })
            } else if (this.isAccountingModule) {
              this.$router.push({
                name: 'AccountingOnboardingView',
                params: { id: this.id },
                query: query
              })
            }
            localStorage.removeItem('newDeviceId')
          } else if (this.nextPath !== null) {
            const next = this.nextPath
            this.nextPath = null
            next()
          }
        } else {
          this.dataProcessing = false
          await this.$store.dispatch('onboarding/setLoading', false)
          const objectsTabIndex = 1
          const tenantsTabIndex = 2
          const isOnlyObjectsInvalid = !isObjectsViewValid && isBuildingViewValid && isTenantsViewValid && isCondominiumsViewValid && isDevicesViewValid && isPortalLinksViewValid
          const isOnlyTenantsInvalid = !isTenantsViewValid && isBuildingViewValid && isObjectsViewValid && isCondominiumsViewValid && isDevicesViewValid && isPortalLinksViewValid
          const suppressGeneric = (isOnlyObjectsInvalid && this.shouldSuppressGenericValidationToast(objectsTabIndex)) ||
            (isOnlyTenantsInvalid && this.shouldSuppressGenericValidationToast(tenantsTabIndex))
          if (!suppressGeneric) {
            Vue.toasted.show(this.$t('message.savingErrors.validationError3'), { type: 'error' })
          }
          if (!isBuildingViewValid) {
            this.tabIndex = 0
          } else if (!isObjectsViewValid) {
            this.tabIndex = 1
          } else if (!isTenantsViewValid) {
            this.tabIndex = 2
          } else if (!isCondominiumsViewValid) {
            this.tabIndex = 2
          } else if (!isDevicesViewValid) {
            this.tabIndex = 3
          } else if (!isPortalLinksViewValid && portalLinksTabIndex !== -1) {
            this.tabIndex = portalLinksTabIndex
          }
        }
      })
    },
    showConfirm() {
      if (this.hasChanged) {
        localStorage.setItem('toReadView', true)
        this.$refs['modal-confirmation'].show()
      } else {
        this.redirectToReadView()
      }
    },
    redirectToReadView() {
      localStorage.setItem('scrollpos', window.scrollY)
      const selectedElement = this.$route.query.elementSelected || this.elementSelected
      const query = {
        view: this.view,
        elementSelected: selectedElement,
        from: this.from,
        objectFilters: this.objectFilters,
        tenantFilters: this.tenantFilters,
        condominiumFilters: this.condominiumFilters,
        deviceFilters: this.deviceFilters,
        historyFilters: this.historyFilters,
        historySubTab: this.historySubTab,
        investPlanFilters: this.investPlanFilters
      }
      if (this.isOwnerModule) {
        this.$router.push({
          name: 'OwnerBuildingDetailsView',
          params: { id: this.id },
          query: query
        }, () => {
        })
      } else if (this.isAdminModule) {
        this.$router.push({
          name: 'AdminBuildingDetailsView',
          params: { id: this.id },
          query: query
        }, () => {
        })
      } else if (this.isAssetModule) {
        this.$router.push({
          name: 'AssetBuildingDetailsView',
          params: { id: this.id },
          query: query
        }, () => {
        })
      } else if (this.isAccountingModule) {
        this.$router.push({
          name: 'AccountingBuildingDetailsView',
          params: { id: this.id },
          query: query
        }, () => {
        })
      }
    },
    updateDevice(item) {
      this.$set(this.buildings[0].devices, item.index, item.device)
    },
    updateElementSelected(item) {
      if (item && item.id && this.elementSelected !== item.id) {
        const query = {
          view: this.view,
          elementSelected: item.id,
          objectFilters: this.objectFilters,
          tenantFilters: this.tenantFilters,
          condominiumFilters: this.condominiumFilters,
          deviceFilters: this.deviceFilters,
          historyFilters: this.historyFilters
        }
        if (this.isOwnerModule) {
          // this.$router.push({ name: 'OwnerOnboardingView', params: { id: this.id }, query: query })
          this.$route.query.elementSelected = item.id
          const route = this.$router.resolve({
            name: 'OwnerOnboardingView',
            params: { id: this.id },
            query: query
          })
          history.replaceState(
            {},
            null,
            `${route.href}`
          )
        } else if (this.isAdminModule) {
          // this.$router.push({ nam/**/e: 'AdminOnboardingView', params: { id: this.id }, query: query })
          this.$route.query.elementSelected = item.id
          const route = this.$router.resolve({
            name: 'AdminOnboardingView',
            params: { id: this.id },
            query: query
          })
          history.replaceState(
            {},
            null,
            `${route.href}`
          )
        } else if (this.isAssetModule) {
          // this.$router.push({ name: 'AssetOnboardingView', params: { id: this.id }, query: query })
          this.$route.query.elementSelected = item.id
          const route = this.$router.resolve({
            name: 'AssetOnboardingView',
            params: { id: this.id },
            query: query
          })
          history.replaceState(
            {},
            null,
            `${route.href}`
          )
        } else if (this.isAccountingModule) {
          // this.$router.push({ name: 'AccountingOnboardingView', params: { id: this.id }, query: query })
          this.$route.query.elementSelected = item.id
          const route = this.$router.resolve({
            name: 'AccountingOnboardingView',
            params: { id: this.id },
            query: query
          })
          history.replaceState(
            {},
            null,
            `${route.href}`
          )
        }
      }
    },
    updateWithFilter(newVal, field) {
      const query = {
        view: this.view,
        elementSelected: this.elementSelected,
        objectFilters: this.objectFilters,
        tenantFilters: this.tenantFilters,
        condominiumFilters: this.condominiumFilters,
        deviceFilters: this.deviceFilters,
        historyFilters: this.historyFilters,
        historySubTab: this.historySubTab,
        addNewItemDevice: this.addNewItemDevice === true || this.addNewItemDevice === 'true',
        addNewComponent: this.addNewComponent === true || this.addNewComponent === 'true',
        investPlanFilters: this.investPlanFilters
      }
      const initQuery = JSON.stringify(query)
      query[field] = newVal
      if (initQuery !== JSON.stringify(query)) {
        if (this.isOwnerModule) {
          this.$router.push({
            name: 'OwnerOnboardingView',
            params: { id: this.id },
            query: query
          })
        } else if (this.isAssetModule) {
          this.$router.push({
            name: 'AssetOnboardingView',
            params: { id: this.id },
            query: query
          })
        } else if (this.isAccountingModule) {
          this.$router.push({
            name: 'AccountingOnboardingView',
            params: { id: this.id },
            query: query
          })
        } else {
          this.$router.push({
            name: 'AdminOnboardingView',
            params: { id: this.id },
            query: query
          })
        }
      }
    },
    updateWithAddressComponents(addressComponents) {
      const streetNo = addressComponents.find((component) => component.types.find((type) => type === 'street_number'))
      const street = addressComponents.find((component) => component.types.find((type) => type === 'route'))
      const city = addressComponents.find((component) => component.types.find((type) => type === 'locality'))
      const zip = addressComponents.find((component) => component.types.find((type) => type === 'postal_code'))
      const state = addressComponents.find((component) => component.types.find((type) => type === 'administrative_area_level_1'))
      const country = addressComponents.find((component) => component.types.find((type) => type === 'country'))

      if (country && country.short_name === 'CH') {
        if (streetNo) {
          this.buildings[0].address.streetNo = streetNo.long_name
        }
        if (street) {
          this.buildings[0].address.street = street.long_name
        }
        if (city) {
          this.buildings[0].address.city = city.long_name
        }
        if (zip) {
          this.buildings[0].address.zip = zip.long_name
        }
        if (state) {
          this.buildings[0].address.state = state.short_name
        }
      } else {
        // TODO Alert
      }
    },
    getBuildingRoute(view) {
      const selectedElement = this.$route.query?.elementSelected !== '' ? this.$route.query.elementSelected : this.elementSelected
      if (this.isOwnerModule) {
        return {
          name: 'OwnerOnboardingView',
          params: { id: this.id },
          query: {
            view: view,
            elementSelected: selectedElement
          }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminOnboardingView',
          params: { id: this.id },
          query: {
            view: view,
            elementSelected: selectedElement
          }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetOnboardingView',
          params: { id: this.id },
          query: {
            view: view,
            elementSelected: selectedElement
          }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingOnboardingView',
          params: { id: this.id },
          query: {
            view: view,
            elementSelected: selectedElement
          }
        }
      }
    }
  },
  validations: {
    buildings: {
      required,
      $each: {
        address: {
          street: {
            required
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.floating-wrapper {
  right: 5vw;
  bottom: 4vh;
  z-index: 9999;
  position: fixed;

  .floating-button {
    border-radius: 100px !important;
    display: flex;
    align-items: center;
    box-shadow: 0 10px 10px rgba(0, 0, 0, 0.20), 0 4px 4px rgba(0, 0, 0, 0.15);
    z-index: 2;
    justify-content: center;
    background-color: $color-primary-dark;
    padding: 20px !important;
    cursor: pointer;

    svg {
      color: white;
      font-size: 15px;
    }
  }
}

.inputs-container {
  overflow-y: scroll;
  height: calc(100vh - 100px);
}

.item {
  cursor: pointer;
}

:deep(.collapse-title) {
  &.collapsed {
    h5 {
      background-image: url('../../assets/arrow_down.svg');
    }
  }

  h5 {
    background-image: url('../../assets/arrow_up.svg');
    background-repeat: no-repeat;
    font-size: large;
    font-weight: 500 !important;
    height: 1.2rem;
    margin-top: 0.2rem;
    margin-bottom: 0.2rem;
    background-position: right;
  }
}

:deep(.global-card) {
  margin-bottom: 0;

  & > .card-body {
    -webkit-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
    -moz-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
    box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
  }

  border-left: 0;
  border-right: 0;
  border-bottom: 0;

  &.no-top-border {
    border-top: 0;
  }
}

:deep(.tabs .nav-tabs .nav-link a.router-link-active), :deep(.tabs .nav-tabs .nav-link a) {
  color: inherit !important;
  text-decoration: none !important;
}
.adaptToPropertyButton {
  // check css on staging before change it
  top: 40px;
  max-height: 30px;
}
</style>
